/**
 * Agreement horizon of two floor curves (W1-05b engine-graph; ARCHITECTURE.md §7.1 "maxTime changed", F§7.2,
 * F§7.3).
 *
 * When max_time moves, both integrators re-segment the floor curve at the new max_time (legacy: max_time is one of
 * the keyframe times; 170: the final `(max_time, height, HOLD)` keyframe, and speed events crossing max_time are
 * interpolated toward it). Everything before the segment containing the earlier of the two max_time values keeps
 * its keyframes, so heights there are bit-identical. The graph only needs the note runtimes (stored heights) of
 * lines whose notes sample the floor at or after that point; the others keep theirs.
 *
 * Instead of assuming where an integrator re-segments, `floorHorizon(a, b)` compares the two curves' keyframe
 * data directly and returns T such that `a.heightAt(t)` and `b.heightAt(t)` are bit-identical for every t < T:
 *
 *   - per track (the 170 integrator has one per speed layer; their heights are summed in the same order): the
 *     longest common prefix of keyframes with identical time, f32 height and tween (every field of the tween
 *     tables: kind, slice constants, integral constants, coefficients; bezier curves by control points);
 *   - a segment i of `heightAt` reads keyframes i, i + 1 and tween i, and both cursors (Phira's sorted search and
 *     the forward walk of unsorted tracks) pick a segment below the first keyframe with time > t, so with keyframes
 *     0..j and tweens 0..j-1 identical, every t < times[j] evaluates identically (j >= 1; extrapolation before the
 *     first keyframe uses segment 0, so j = 0 guarantees nothing);
 *   - a track that is sorted in one curve and unsorted in the other differs only at t = 0 (the forward walk keeps
 *     cursor 0 there), which is checked by evaluation;
 *   - identical tracks agree everywhere (+Infinity); different kinds, track counts or a NaN horizon give -Infinity.
 *
 * NaN times are never covered (`NaN < T` is false): callers treat a NaN sample time as "after the horizon".
 */

import type { TweenTable } from './anim';
import type { FloorCurve, FloorTrack, FloorTweenTable } from './floor/curve';

function same(a: number | undefined, b: number | undefined): boolean {
  return Object.is(a, b);
}

/** Bezier curves evaluate identically when their f32 control points are identical. */
function sameCurve(a: TweenTable['curves'][number] | undefined, b: TweenTable['curves'][number] | undefined): boolean {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  const p = a.p;
  const q = b.p;
  return same(p[0], q[0]) && same(p[1], q[1]) && same(p[2], q[2]) && same(p[3], q[3]);
}

/** Entry i of two keyframe tween tables is identical in every field `y` / `dy` read. */
function sameTween(a: TweenTable, i: number, b: TweenTable, j: number): boolean {
  return (
    a.kind[i] === b.kind[j] &&
    a.id[i] === b.id[j] &&
    same(a.lo[i], b.lo[j]) &&
    same(a.span[i], b.span[j]) &&
    same(a.fl[i], b.fl[j]) &&
    same(a.den[i], b.den[j]) &&
    sameCurve(a.curves[i], b.curves[j])
  );
}

/** Floor tween i of two tables is identical (kind, event tween, integral, coefficients, total). */
export function sameFloorTween(a: FloorTweenTable, b: FloorTweenTable, i: number): boolean {
  return (
    a.kind[i] === b.kind[i] &&
    a.intKind[i] === b.intKind[i] &&
    a.intId[i] === b.intId[i] &&
    same(a.intL[i], b.intL[i]) &&
    same(a.intR[i], b.intR[i]) &&
    same(a.c0[i], b.c0[i]) &&
    same(a.c1[i], b.c1[i]) &&
    same(a.total[i], b.total[i]) &&
    sameTween(a.f, i, b.f, i)
  );
}

/** Agreement horizon of two keyframe tracks (see the module comment). */
export function trackHorizon(a: FloorTrack, b: FloorTrack): number {
  if (a === b) return Number.POSITIVE_INFINITY;
  const na = a.length;
  const nb = b.length;
  if (na === 0 || nb === 0) return na === nb ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  if (na === 1 || nb === 1) {
    // One keyframe: its height everywhere (a lone keyframe never interpolates).
    return na === nb && same(a.values[0], b.values[0]) ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  }
  const n = Math.min(na, nb);
  // m: leading keyframes with identical time and height.
  let m = 0;
  while (m < n && same(a.times[m], b.times[m]) && same(a.values[m], b.values[m])) m++;
  // k: leading identical tweens among the m - 1 segments between those keyframes.
  let k = 0;
  while (k < m - 1 && sameFloorTween(a.tweens, b.tweens, k)) k++;
  const j = Math.min(m - 1, k);
  let horizon: number;
  if (na === nb && m === na && k >= na - 1) horizon = Number.POSITIVE_INFINITY;
  else if (j <= 0) return Number.NEGATIVE_INFINITY;
  else horizon = a.times[j] ?? Number.NaN;
  if (Number.isNaN(horizon)) return Number.NEGATIVE_INFINITY;
  if (a.sorted !== b.sorted && horizon > 0) {
    // The two cursor algorithms disagree only at t = 0 (and -0).
    if (!same(a.heightAt(0), b.heightAt(0)) || !same(a.heightAt(-0), b.heightAt(-0))) horizon = 0;
  }
  return horizon;
}

/**
 * The time T before which two floor curves give bit-identical heights (+Infinity: identical everywhere,
 * -Infinity: no guarantee). Only `heightAt` is compared (what note runtimes store); `speedAt` readouts are
 * recomputed by the queries anyway.
 */
export function floorHorizon(a: FloorCurve, b: FloorCurve): number {
  if (a === b) return Number.POSITIVE_INFINITY;
  if (a.kind !== b.kind || a.tracks.length !== b.tracks.length) return Number.NEGATIVE_INFINITY;
  let horizon = Number.POSITIVE_INFINITY;
  for (let i = 0; i < a.tracks.length; i++) {
    const ta = a.tracks[i];
    const tb = b.tracks[i];
    if (ta === undefined || tb === undefined) return Number.NEGATIVE_INFINITY;
    const h = trackHorizon(ta, tb);
    if (h < horizon) horizon = h;
    if (horizon === Number.NEGATIVE_INFINITY) break;
  }
  return horizon;
}
